import { useMemo } from 'react';
import { JOIN_APP_EXPREALTY } from 'constants/urls';
import { GTM_CLICK_CONTACT_US_BUTTON_JOIN_EXP, GTM_CLICK_IM_READY_BUTTON_JOIN_EXP } from 'constants/events';
import { useIsMobile, useIsLargeMobile } from './use-size-class';

type MenuItem = {
  id: string;
  label: string;
  link?: string;
  newTab?: boolean;
  mobileOnly?: boolean;
  onClick?: () => void;
};

type ButtonLabels = {
  imReady?: string;
  contactUs?: string;
  commercialWaitlist?: string;
};

type UseJoinExpMenuItemsParams = {
  isJoinExp: boolean;
  baseMenuItems: MenuItem[];
  buttonLabels?: ButtonLabels;
  joinPageContactPath?: string;
  joinPageContactPage?: string;
  trackExpEvent: (event: string) => void;
};

/**
 * Custom hook to manage menu items for Join eXp pages with responsive button behavior.
 * 
 * Handles the logic for showing/hiding "I'm Ready" and "Contact Us" buttons based on screen size:
 * - Small mobile (≤380px): Both buttons in menu
 * - Medium mobile (381-768px): "Contact Us" as button, "I'm Ready" in menu
 * - Desktop (>768px): Both buttons as buttons
 * 
 * @param params Configuration object
 * @returns Object containing menuItems and visibility flags for buttons
 */
export function useJoinExpMenuItems({
  isJoinExp,
  baseMenuItems,
  buttonLabels,
  joinPageContactPath,
  joinPageContactPage,
  trackExpEvent,
}: UseJoinExpMenuItemsParams) {
  const isMobile = useIsMobile();
  const isLargeMobile = useIsLargeMobile();

  const getJoinExpContactUrl = () => {
    if (isJoinExp) {
      return joinPageContactPath;
    }
    return joinPageContactPage;
  };

  // Determine which buttons to show based on screen size
  const shouldShowContactUsButton = isJoinExp && (isLargeMobile || !isMobile);
  const shouldShowImReadyButton = isJoinExp && !isMobile;

  // Add Join eXp buttons to menu items when they're not shown as buttons
  const menuItems = useMemo(() => {
    if (!isJoinExp) return baseMenuItems;

    const additionalItems: MenuItem[] = [];
    
    // Add "I'm Ready" to menu if not shown as button
    if (!shouldShowImReadyButton) {
      additionalItems.push({
        id: 'im-ready',
        label: buttonLabels?.imReady || "I'm Ready",
        link: JOIN_APP_EXPREALTY,
        newTab: true,
        onClick: () => trackExpEvent(GTM_CLICK_IM_READY_BUTTON_JOIN_EXP),
      });
    }

    // Add "Contact Us" to menu if not shown as button
    if (!shouldShowContactUsButton) {
      additionalItems.push({
        id: 'contact-us',
        label: buttonLabels?.contactUs || 'Contact Us',
        link: getJoinExpContactUrl(),
        newTab: true,
        onClick: () => trackExpEvent(GTM_CLICK_CONTACT_US_BUTTON_JOIN_EXP),
      });
    }

    // Add items at the beginning of the menu
    return [...additionalItems, ...baseMenuItems];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isJoinExp, isMobile, isLargeMobile, baseMenuItems, buttonLabels, joinPageContactPath, joinPageContactPage]);

  return {
    menuItems,
    shouldShowContactUsButton,
    shouldShowImReadyButton,
    getJoinExpContactUrl,
  };
}

